import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Bell, 
  Mail, 
  MessageSquare, 
  Calendar,
  Save,
  ArrowLeft,
  Smartphone,
  Monitor,
  Volume2,
  VolumeX
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/contexts/AuthContext'

interface NotificationSettings {
  email: {
    announcements: boolean
    assignments: boolean
    grades: boolean
    discussions: boolean
    reminders: boolean
  }
  push: {
    announcements: boolean
    assignments: boolean
    grades: boolean
    discussions: boolean
    reminders: boolean
  }
  inApp: {
    announcements: boolean
    assignments: boolean
    grades: boolean
    discussions: boolean
    reminders: boolean
  }
  frequency: 'immediate' | 'daily' | 'weekly' | 'never'
  quietHours: {
    enabled: boolean
    start: string
    end: string
  }
}

export function NotificationSettingsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isSaving, setIsSaving] = useState(false)
  
  const [settings, setSettings] = useState<NotificationSettings>({
    email: {
      announcements: true,
      assignments: true,
      grades: true,
      discussions: false,
      reminders: true
    },
    push: {
      announcements: true,
      assignments: true,
      grades: true,
      discussions: true,
      reminders: false
    },
    inApp: {
      announcements: true,
      assignments: true,
      grades: true,
      discussions: true,
      reminders: true
    },
    frequency: 'immediate',
    quietHours: {
      enabled: false,
      start: '22:00',
      end: '08:00'
    }
  })

  const handleToggle = (category: keyof NotificationSettings, field: string) => {
    if (category === 'frequency' || category === 'quietHours') return
    
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...(prev[category] as any),
        [field]: !(prev[category] as any)[field]
      }
    }))
  }

  const handleFrequencyChange = (frequency: 'immediate' | 'daily' | 'weekly' | 'never') => {
    setSettings(prev => ({
      ...prev,
      frequency
    }))
  }

  const handleQuietHoursToggle = () => {
    setSettings(prev => ({
      ...prev,
      quietHours: {
        ...prev.quietHours,
        enabled: !prev.quietHours.enabled
      }
    }))
  }

  const handleQuietHoursChange = (field: 'start' | 'end', value: string) => {
    setSettings(prev => ({
      ...prev,
      quietHours: {
        ...prev.quietHours,
        [field]: value
      }
    }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000))
      // In a real app, save settings to backend
    } catch (error) {
      console.error('Save error:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const notificationTypes = [
    { key: 'announcements', label: 'Announcements', description: 'Important updates and news' },
    { key: 'assignments', label: 'Assignments', description: 'New assignments and due date reminders' },
    { key: 'grades', label: 'Grades', description: 'Grade updates and feedback' },
    { key: 'discussions', label: 'Discussions', description: 'New posts and replies in discussions' },
    { key: 'reminders', label: 'Reminders', description: 'General reminders and notifications' }
  ]

  const channels = [
    { key: 'email', label: 'Email', icon: Mail, description: 'Receive notifications via email' },
    { key: 'push', label: 'Push Notifications', icon: Smartphone, description: 'Mobile push notifications' },
    { key: 'inApp', label: 'In-App', icon: Monitor, description: 'Notifications within the application' }
  ]

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-3 sm:space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/more')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Notification Settings</h1>
            <p className="text-sm sm:text-base text-gray-600">Configure how you receive notifications</p>
          </div>
        </div>
        <Button 
          onClick={handleSave}
          disabled={isSaving}
          className="w-full sm:w-auto bg-primary-600 hover:bg-primary-700"
        >
          {isSaving ? (
            <>
              <Save className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
        {/* Notification Channels */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {channels.map((channel) => (
            <Card key={channel.key} className="p-4 sm:p-6">
              <div className="flex items-center space-x-3 mb-3 sm:mb-4">
                <channel.icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">{channel.label}</h3>
                  <p className="text-xs sm:text-sm text-gray-600">{channel.description}</p>
                </div>
              </div>
              
              <div className="space-y-2 sm:space-y-3">
                {notificationTypes.map((type) => (
                  <div key={type.key} className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm sm:text-base font-medium text-gray-900">{type.label}</h4>
                      <p className="text-xs sm:text-sm text-gray-600">{type.description}</p>
                    </div>
                    <button
                      onClick={() => handleToggle(channel.key as keyof NotificationSettings, type.key)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        (settings[channel.key as keyof NotificationSettings] as any)[type.key]
                          ? 'bg-primary-600'
                          : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          (settings[channel.key as keyof NotificationSettings] as any)[type.key]
                            ? 'translate-x-6'
                            : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>

        {/* Settings Sidebar */}
        <div className="space-y-6">
          {/* Frequency Settings */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Frequency</h3>
            <div className="space-y-3">
              {[
                { value: 'immediate', label: 'Immediate', description: 'Get notified right away' },
                { value: 'daily', label: 'Daily Digest', description: 'Once per day summary' },
                { value: 'weekly', label: 'Weekly Summary', description: 'Once per week summary' },
                { value: 'never', label: 'Never', description: 'Disable all notifications' }
              ].map((option) => (
                <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="frequency"
                    value={option.value}
                    checked={settings.frequency === option.value}
                    onChange={() => handleFrequencyChange(option.value as any)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">{option.label}</div>
                    <div className="text-sm text-gray-600">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </Card>

          {/* Quiet Hours */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quiet Hours</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Enable Quiet Hours</h4>
                  <p className="text-sm text-gray-600">Pause notifications during specified hours</p>
                </div>
                <button
                  onClick={handleQuietHoursToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.quietHours.enabled ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.quietHours.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              {settings.quietHours.enabled && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={settings.quietHours.start}
                      onChange={(e) => handleQuietHoursChange('start', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Time
                    </label>
                    <input
                      type="time"
                      value={settings.quietHours.end}
                      onChange={(e) => handleQuietHoursChange('end', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Test Notifications */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Notifications</h3>
            <p className="text-sm text-gray-600 mb-4">
              Send yourself a test notification to verify your settings
            </p>
            <Button variant="outline" className="w-full">
              <Bell className="h-4 w-4 mr-2" />
              Send Test Notification
            </Button>
          </Card>
        </div>
      </div>
    </div>
  )
}